import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ROLE_TR } from "@/utils/format";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const empty = { email: "", password: "", name: "", role: "sales" };

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const isEdit = !!editingId;

  const load = async () => {
    try { const { data } = await api.get("/users"); setRows(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const startEdit = (u) => {
    setForm({ email: u.email, name: u.name, role: u.role, password: "" });
    setEditingId(u.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (isEdit) {
        // Only include password if user typed one
        const payload = { email: form.email, name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
        toast.success("Kullanıcı güncellendi");
      } else {
        await api.post("/users", form);
        toast.success("Kullanıcı eklendi");
      }
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (u.id === me?.id) { toast.error("Kendi hesabınızı silemezsiniz"); return; }
    if (!window.confirm(`${u.name} silinsin mi?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between border-b-2 border-[#09090B] pb-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500">Erişim Kontrolü</div>
          <h1 className="text-4xl font-black tracking-tighter font-heading">Kullanıcılar</h1>
        </div>
        <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={startCreate} data-testid="new-user-btn">
          <Plus size={16} className="mr-2" /> Yeni Kullanıcı
        </Button>
      </div>

      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">Ad</TableHead>
              <TableHead className="font-mono uppercase text-xs">E-posta</TableHead>
              <TableHead className="font-mono uppercase text-xs">Rol</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-semibold">
                  {u.name}
                  {u.id === me?.id && <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase border border-[#0055FF] text-[#0055FF]">Siz</span>}
                </TableCell>
                <TableCell className="font-mono">{u.email}</TableCell>
                <TableCell><span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase border border-[#09090B]">{ROLE_TR[u.role]}</span></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil size={14} /></Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => remove(u)}
                    disabled={u.id === me?.id}
                    data-testid={`delete-user-${u.id}`}
                  >
                    <Trash2 size={14} className={u.id === me?.id ? "text-neutral-300" : "text-red-600"} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-2 border-[#09090B] bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              {isEdit ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs font-mono uppercase">Ad</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none border-2 border-[#09090B]" data-testid="user-name-input" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">E-posta</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-none border-2 border-[#09090B] font-mono" data-testid="user-email-input" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">
                Şifre {isEdit && <span className="normal-case font-normal text-neutral-500 tracking-normal ml-1">(boş bırakılırsa değişmez)</span>}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={isEdit ? "Değiştirmek için yeni şifre girin" : ""}
                className="rounded-none border-2 border-[#09090B] font-mono"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Rol</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
                disabled={isEdit && editingId === me?.id}
              >
                <SelectTrigger className="rounded-none border-2 border-[#09090B] bg-white" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-2 border-[#09090B] bg-white">
                  <SelectItem value="admin">Yönetici</SelectItem>
                  <SelectItem value="sub_admin">Alt Yönetici</SelectItem>
                  <SelectItem value="sales">Satış</SelectItem>
                  <SelectItem value="warehouse">Depo</SelectItem>
                  <SelectItem value="production">Üretim</SelectItem>
                  <SelectItem value="accounting">Muhasebe</SelectItem>
                  <SelectItem value="hse">İSG Personeli</SelectItem>
                </SelectContent>
              </Select>
              {isEdit && editingId === me?.id && (
                <div className="text-[10px] font-mono text-neutral-500 mt-1">Kendi rolünüzü değiştiremezsiniz</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={save} data-testid="save-user-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
